<template>
  <div
    class="bar-chart-container"
    :class="{ loading: isLoading, error: hasError }"
    :style="containerStyles"
  >
    <!-- Loading State -->
    <div v-if="isLoading" class="chart-loading">
      <div class="loading-spinner"></div>
      <p>Loading chart data...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="hasError" class="chart-error">
      <div class="error-icon">⚠️</div>
      <p>{{ errorMessage }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="isEmpty" class="chart-empty">
      <div class="empty-icon">📊</div>
      <p>No data available</p>
    </div>

    <!-- Chart Component -->
    <Bar
      v-else
      :chart-options="computedChartOptions"
      :chart-data="validatedChartData"
      :chart-id="chartId"
      :style="chartStyles"
      :aria-label="ariaLabel"
      role="img"
      @chart:render="onChartRender"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels,
);

/**
 * Props with enhanced validation and configuration options
 */
const props = defineProps({
  // Core data
  chartData: {
    type: Object,
    default: () => ({ datasets: [], labels: [] }),
    validator: (value) => {
      return (
        value &&
        typeof value === "object" &&
        Array.isArray(value.datasets) &&
        Array.isArray(value.labels)
      );
    },
  },

  // Chart identification
  chartId: {
    type: String,
    default: () => `bar-chart-${Math.random().toString(36).substr(2, 9)}`,
  },

  datasetIdKey: {
    type: String,
    default: "label",
  },

  // Styling and layout
  height: {
    type: [String, Number],
    default: 300,
    validator: (value) => {
      return (
        (typeof value === "number" && value > 0) ||
        (typeof value === "string" && value.match(/^\d+(px|%|em|rem)$/))
      );
    },
  },

  width: {
    type: [String, Number],
    default: "100%",
  },

  // Responsive behavior
  responsive: {
    type: Boolean,
    default: true,
  },

  maintainAspectRatio: {
    type: Boolean,
    default: false,
  },

  // Color configuration
  colors: {
    type: Array,
    default: () => ["#ffde00", "#5db0f7", "#5df7b8", "#00D8FF", "#DD1B16"],
    validator: (value) => Array.isArray(value) && value.length > 0,
  },

  // Chart options override
  options: {
    type: Object,
    default: () => ({}),
  },

  // Loading and error states
  loading: {
    type: Boolean,
    default: false,
  },

  error: {
    type: String,
    default: "",
  },

  // Accessibility
  ariaLabel: {
    type: String,
    default: "Horizontal bar chart displaying data visualization",
  },

  // Animation
  animate: {
    type: Boolean,
    default: true,
  },

  animationDuration: {
    type: Number,
    default: 1000,
    validator: (value) => value >= 0,
  },

  // Data labels configuration
  showDataLabels: {
    type: Boolean,
    default: true,
  },

  dataLabelPosition: {
    type: String,
    default: "end",
    validator: (value) => ["start", "center", "end"].includes(value),
  },

  // Axes configuration
  showYAxisLabels: {
    type: Boolean,
    default: true,
  },

  showXAxisLabels: {
    type: Boolean,
    default: false,
  },

  showGrid: {
    type: Boolean,
    default: false,
  },

  // Bar configuration
  maxBarThickness: {
    type: Number,
    default: 40,
    validator: (value) => value > 0,
  },

  // Theme
  theme: {
    type: String,
    default: "light",
    validator: (value) => ["light", "dark"].includes(value),
  },
});

/**
 * Emits
 */
const emit = defineEmits(["chart:render", "chart:error", "chart:ready"]);

/**
 * Reactive state
 */
const isLoading = ref(props.loading);
const hasError = ref(!!props.error);
const errorMessage = ref(props.error);
const chartInstance = ref(null);

/**
 * Computed properties
 */
const isEmpty = computed(() => {
  if (!props.chartData || !props.chartData.datasets) return true;
  return (
    props.chartData.datasets.length === 0 || props.chartData.labels.length === 0
  );
});

const containerStyles = computed(() => ({
  width: typeof props.width === "number" ? `${props.width}px` : props.width,
  height: typeof props.height === "number" ? `${props.height}px` : props.height,
  position: "relative",
}));

const chartStyles = computed(() => ({
  width: "100%",
  height: "100%",
  position: "relative",
}));

const validatedChartData = computed(() => {
  if (!props.chartData || !props.chartData.datasets) {
    return { datasets: [], labels: [] };
  }

  // Apply color scheme to datasets if not already defined
  const datasets = props.chartData.datasets.map((dataset, index) => ({
    ...dataset,
    backgroundColor:
      dataset.backgroundColor || props.colors[index % props.colors.length],
    borderColor:
      dataset.borderColor || props.colors[index % props.colors.length],
    borderWidth: dataset.borderWidth || 1,
  }));

  return {
    ...props.chartData,
    datasets,
  };
});

const computedChartOptions = computed(() => {
  const baseOptions = {
    responsive: props.responsive,
    maintainAspectRatio: props.maintainAspectRatio,
    indexAxis: "y",

    animation: props.animate
      ? {
          duration: props.animationDuration,
          easing: "easeOutQuart",
        }
      : false,

    plugins: {
      legend: {
        display: false,
      },

      tooltip: {
        enabled: true,
        backgroundColor:
          props.theme === "dark" ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.9)",
        titleColor: props.theme === "dark" ? "#ffffff" : "#000000",
        bodyColor: props.theme === "dark" ? "#ffffff" : "#000000",
        borderColor: props.theme === "dark" ? "#555" : "#ddd",
        borderWidth: 1,
        cornerRadius: 4,
      },

      datalabels: props.showDataLabels
        ? {
            anchor: props.dataLabelPosition,
            align: props.dataLabelPosition,
            formatter: (value, context) => {
              if (typeof value === "number") {
                return value.toLocaleString();
              }
              return value;
            },
            font: {
              weight: "bold",
              size: 12,
            },
            color: props.theme === "dark" ? "#ffffff" : "#333333",
          }
        : false,
    },

    scales: {
      x: {
        grid: {
          display: props.showGrid,
          color:
            props.theme === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
          drawBorder: false,
        },
        ticks: {
          display: props.showXAxisLabels,
          color: props.theme === "dark" ? "#ffffff" : "#666666",
        },
      },
      y: {
        grid: {
          display: props.showGrid,
          color:
            props.theme === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
          drawBorder: false,
        },
        ticks: {
          display: props.showYAxisLabels,
          color: props.theme === "dark" ? "#ffffff" : "#666666",
          font: {
            size: 11,
          },
        },
        categoryPercentage: 0.8,
        barPercentage: 0.6,
        maxBarThickness: props.maxBarThickness,
      },
    },

    // Accessibility
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  // Deep merge with user-provided options
  return mergeDeep(baseOptions, props.options);
});

/**
 * Watchers
 */
watch(
  () => props.loading,
  (newVal) => {
    isLoading.value = newVal;
  },
);

watch(
  () => props.error,
  (newVal) => {
    hasError.value = !!newVal;
    errorMessage.value = newVal || "";
    if (newVal) {
      emit("chart:error", newVal);
    }
  },
);

watch(
  () => props.chartData,
  (newData, oldData) => {
    // Validate new data
    if (newData && !validateChartData(newData)) {
      hasError.value = true;
      errorMessage.value = "Invalid chart data format";
      return;
    }

    // Clear any previous errors
    hasError.value = false;
    errorMessage.value = "";
  },
  { deep: true },
);

/**
 * Lifecycle hooks
 */
onMounted(() => {
  // Validate initial data
  if (props.chartData && !validateChartData(props.chartData)) {
    hasError.value = true;
    errorMessage.value = "Invalid chart data format";
    return;
  }

  emit("chart:ready");
});

onUnmounted(() => {
  // Cleanup chart instance if needed
  if (chartInstance.value) {
    chartInstance.value.destroy();
  }
});

/**
 * Methods
 */
function validateChartData(data) {
  if (!data || typeof data !== "object") return false;
  if (!Array.isArray(data.datasets) || !Array.isArray(data.labels))
    return false;

  // Check if datasets have required properties
  return data.datasets.every(
    (dataset) =>
      dataset && typeof dataset === "object" && Array.isArray(dataset.data),
  );
}

function mergeDeep(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = mergeDeep(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
}

function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

function onChartRender(chart) {
  chartInstance.value = chart;
  emit("chart:render", chart);
}

/**
 * Expose methods for parent components
 */
defineExpose({
  refresh: () => {
    if (chartInstance.value) {
      chartInstance.value.update();
    }
  },
  exportChart: (type = "png") => {
    if (chartInstance.value) {
      return chartInstance.value.toBase64Image(type);
    }
    return null;
  },
  getChart: () => chartInstance.value,
});
</script>

<style scoped>
.bar-chart-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border-radius: 8px;
  background: var(--chart-background, #ffffff);
  transition: all 0.3s ease;
}

.bar-chart-container.loading {
  opacity: 0.7;
}

.bar-chart-container.error {
  background: var(--chart-error-background, #fff5f5);
  border: 1px solid var(--chart-error-border, #fed7d7);
}

.chart-loading,
.chart-error,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: var(--chart-text-color, #666);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--chart-primary-color, #ffde00);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error-icon,
.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.chart-loading p,
.chart-error p,
.chart-empty p {
  margin: 0;
  font-size: 1rem;
  color: inherit;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Dark theme support */
.bar-chart-container[data-theme="dark"] {
  --chart-background: #1a1a1a;
  --chart-text-color: #ffffff;
  --chart-error-background: #2d1b1b;
  --chart-error-border: #4a2c2c;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bar-chart-container {
    min-height: 150px;
  }

  .chart-loading,
  .chart-error,
  .chart-empty {
    padding: 1rem;
  }

  .loading-spinner {
    width: 30px;
    height: 30px;
  }

  .error-icon,
  .empty-icon {
    font-size: 2rem;
  }
}

/* Print styles */
@media print {
  .bar-chart-container {
    break-inside: avoid;
    background: white !important;
  }

  .chart-loading,
  .chart-error {
    display: none;
  }
}
</style>
